/**
 * Weapon clash (lane C0-WEAPON; the drawing is render/fx/clash.ts): steel meeting steel rings out, louder and longer
 * with the blow's weight (data/hitWeight.ts); a palm or fist meeting a blade is a dull thud with no ring. Played for
 * the shared `hit` events that end on a guard (blocked, guard break, interrupt) and the duel's parry and counter, once
 * sfx/events.ts routes them here (main's edit, see the lane report).
 */
import { registerSfx } from '../registry';

/** Steel on steel: a hard tick, the ring (two inharmonic partial sets), a short scrape; `w` 0 light, 1 strong, 2 heavy. */
function ring(k: Parameters<Parameters<typeof registerSfx>[2]>[0], w: 0 | 1 | 2): void {
  const v = [0.8, 1, 1.25][w]!;
  k.noise({ dur: 0.02 + 0.01 * w, vol: 0.34 * v, attack: 0.001, type: 'highpass', f: 3800 });
  k.metal({ f: [1900, 1600, 1280][w]!, dur: [0.45, 0.75, 1.1][w]!, vol: 0.12 * v });
  k.metal({ f: [2740, 2310, 1870][w]!, at: 0.005, dur: [0.25, 0.4, 0.6][w]!, vol: 0.06 * v });
  // The edges grinding apart.
  k.noise({ at: 0.012, dur: 0.08 + 0.05 * w, vol: 0.08 * v, attack: 0.008, f: 5600, f1: 2600, q: 5 });
  k.thump({ f0: 240, f1: 100, dur: 0.07 + 0.03 * w, vol: 0.16 * v });
  if (w === 2) k.thump({ f0: 110, f1: 40, at: 0.01, dur: 0.3, vol: 0.3 });
}

registerSfx('combat.clash.light', { label: 'อาวุธปะทะ (เบา)', cat: 'combat', vary: 0.05, gap: 0.04 }, (k) =>
  ring(k, 0),
);
registerSfx('combat.clash.strong', { label: 'อาวุธปะทะ (หนัก)', cat: 'combat', vary: 0.04, gap: 0.05 }, (k) =>
  ring(k, 1),
);
registerSfx(
  'combat.clash.heavy',
  { label: 'อาวุธปะทะ (หนักมาก)', cat: 'combat', vary: 0.03, gap: 0.08, preview: 1.2 },
  (k) => ring(k, 2),
);

registerSfx(
  'combat.clash.dull',
  { label: 'ฝ่ามือปะทะอาวุธ (ทึบ)', cat: 'combat', vary: 0.06, gap: 0.04 },
  (k, o) => {
    // A palm slapping the flat of a blade or a forearm block: a padded thock, a dry cloth snap, no ring.
    const v = Math.min(1.3, o.intensity ?? 1);
    k.thump({ f0: 190, f1: 70, dur: 0.14, vol: 0.32 * v });
    k.noise({ dur: 0.05, vol: 0.2 * v, attack: 0.002, f: 900, q: 1.2 });
    k.noise({ at: 0.01, dur: 0.04, vol: 0.09 * v, attack: 0.001, type: 'highpass', f: 2400 });
    k.tone({ type: 'triangle', f: 420, f1: 260, dur: 0.06, vol: 0.05 * v });
  },
);
